import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Box,
	Typography,
	CircularProgress,
	TextField,
	Grid,
	InputAdornment,
	FormControl,
	InputLabel,
	Select,
	MenuItem
	// Tooltip
} from '@mui/material';
import { useState, useEffect } from 'react';
import { useAnalyzeImageMutation } from '@/store/services/applications';
import { toast } from 'react-toastify';
import heic2any from 'heic2any';

interface ImageAIDialogProps {
	open: boolean;
	onClose: () => void;
	images: (File | string | { product_image: string })[];
	modelNumber?: string;
	description?: string;
	items?: string[];
	selectedItem?: string;
	onUseAIInfo?: (description: string, cost: number, matchedItem: string) => void;
	preAnalyzedData?: {
		choices: {
			message: {
				content: string;
			};
		}[];
	};
}

interface AIResponse {
	choices: {
		message: {
			content: string;
		};
	}[];
}

interface ParsedAIResponse {
	description: string;
	current_estimated_cost: number;
	matched_item: string;
}

const convertToJpg = async (file: File): Promise<File> => {
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.src = URL.createObjectURL(file);
		img.onload = () => {
			const canvas = document.createElement('canvas');
			canvas.width = img.width;
			canvas.height = img.height;
			const ctx = canvas.getContext('2d');

			if (!ctx) {
				reject(new Error('Failed to get canvas context'));
				return;
			}

			ctx.drawImage(img, 0, 0);
			canvas.toBlob(
				(blob) => {
					if (!blob) {
						reject(new Error('Failed to convert image'));
						return;
					}

					const newFile = new File([blob], file.name.replace(/\.[^/.]+$/, '.jpg'), { type: 'image/jpeg' });
					resolve(newFile);
				},
				'image/jpeg',
				0.9
			);
		};
		img.onerror = () => reject(new Error('Failed to load image'));
	});
};

const isValidImageFormat = (file: File): boolean => {
	const validFormats = ['.jpg', '.jpeg', '.png', '.webp'];
	const extension = '.' + file.name.split('.').pop()?.toLowerCase();
	return validFormats.includes(extension);
};

export default function ImageAIDialog({
	open,
	onClose,
	images,
	modelNumber,
	description,
	items,
	selectedItem,
	onUseAIInfo,
	preAnalyzedData
}: ImageAIDialogProps) {
	const [selectedImage, setSelectedImage] = useState<number | null>(null);
	const [analyzeImage] = useAnalyzeImageMutation();
	const [aiResponse, setAiResponse] = useState<AIResponse | null>(null);
	const [parsedResponse, setParsedResponse] = useState<ParsedAIResponse | null>(null);
	const [isAnalyzing, setIsAnalyzing] = useState(false);
	const [editedDescription, setEditedDescription] = useState('');
	const [editedCost, setEditedCost] = useState('');
	const [matchedItem, setMatchedItem] = useState('');
	const [error, setError] = useState<string | null>(null);
	const [isSuccess, setIsSuccess] = useState(false);
	const [processedImages, setProcessedImages] = useState<string[]>([]);

	// Reset states only when dialog is closed and Use this info was clicked
	useEffect(() => {
		if (!open && !isSuccess) {
			setSelectedImage(null);
			setAiResponse(null);
			setParsedResponse(null);
			setIsAnalyzing(false);
			setEditedDescription('');
			setEditedCost('');
			setMatchedItem('');
			setError(null);
		}
	}, [open, isSuccess]);

	// Handle pre-analyzed data when dialog opens
	useEffect(() => {
		if (open && preAnalyzedData) {
			setAiResponse(preAnalyzedData);
			setIsSuccess(true);
			try {
				const parsedContent = JSON.parse(preAnalyzedData.choices[0].message.content);
				setParsedResponse(parsedContent);
				setEditedDescription(parsedContent.description);
				setEditedCost(
					parsedContent.current_estimated_cost?.toString() || parsedContent.estimated_cost?.toString() || ''
				);
				setMatchedItem(parsedContent.matched_item || '');
			} catch (error) {
				setError('Failed to parse AI response');
				toast.error('Failed to parse AI response');
				onClose();
				setIsSuccess(false);
				console.error('Error parsing AI response:', error);
			}
		}
	}, [open, preAnalyzedData]);

	// Process images when dialog opens
	useEffect(() => {
		const processImages = async () => {
			const processed = await Promise.all(
				images.map(async (image) => {
					if (image instanceof File) {
						const isHEIC =
							image.name.toLowerCase().endsWith('.heic') || image.name.toLowerCase().endsWith('.heif');

						if (isHEIC) {
							try {
								const convertedBlob = await heic2any({
									blob: image,
									toType: 'image/jpeg',
									quality: 0.8
								});
								return URL.createObjectURL(convertedBlob as Blob);
							} catch (error) {
								console.error('Error converting HEIC file:', error);
								return URL.createObjectURL(image);
							}
						}

						return URL.createObjectURL(image);
					} else if (typeof image === 'string') {
						return image;
					} else {
						return image.product_image;
					}
				})
			);
			setProcessedImages(processed);
		};

		if (open) {
			processImages();
		}
	}, [open, images]);

	// Cleanup object URLs when dialog closes
	useEffect(() => {
		return () => {
			processedImages.forEach((url) => {
				if (url.startsWith('blob:')) {
					URL.revokeObjectURL(url);
				}
			});
		};
	}, [processedImages]);

	const handleImageSelect = (index: number) => {
		if (!open || isAnalyzing || isSuccess) return;

		setSelectedImage(index);
		setAiResponse(null);
		setParsedResponse(null);
		setEditedDescription('');
		setEditedCost('');
		setMatchedItem('');
	};

	const handleAnalyze = async () => {
		if (!open || isSuccess) return;

		if (selectedImage === null) {
			toast.error('Please select an image to analyze');
			return;
		}

		setIsAnalyzing(true);
		setError(null);

		try {
			const image = images[selectedImage];
			const formData = new FormData();

			if (image instanceof File) {
				let processedFile = image;

				if (!isValidImageFormat(image)) {
					try {
						processedFile = await convertToJpg(image);
					} catch (error) {
						setError('Failed to convert image format');
						toast.error('Failed to convert image format');
						return;
					}
				}

				formData.append('image', processedFile);
			} else if (typeof image === 'string') {
				const response = await fetch(image);
				const blob = await response.blob();
				const file = new File([blob], 'image.jpg', { type: blob.type });

				if (!isValidImageFormat(file)) {
					try {
						const convertedFile = await convertToJpg(file);
						formData.append('image', convertedFile);
					} catch (error) {
						setError('Failed to convert image format');
						toast.error('Failed to convert image format');
						return;
					}
				} else {
					formData.append('image', file);
				}
			} else if (image?.product_image) {
				// Update case: send image URL for server to fetch (avoids CORS from client-side fetch)
				formData.append('image_url', image.product_image);
			}

			// Append modelNumber, description & selected item for analyzeImage API (handles upload + analysis)
			if (modelNumber != null) formData.append('modelNumber', modelNumber);
			if (description != null) formData.append('description', description);
			// Send only the selected item, not the whole array
			formData.append('items[]', selectedItem || 'Other');

		// analyzeImage API handles image upload + analysis, returns analysis result
		const response = await analyzeImage(formData).unwrap();
		const analysis =
			response?.data?.analysis ||
			response?.analysis ||
			response;

		if (!analysis?.choices || analysis.choices.length === 0) {
			setError(response?.error || 'Failed to analyze');
			toast.error(response?.error || 'Failed to analyze');
				onClose();
				setIsSuccess(false);
			} else {
			setAiResponse(analysis);
				setIsSuccess(true);

				try {
				const parsedContent = JSON.parse(analysis.choices[0].message.content);
					setParsedResponse(parsedContent);
					setEditedDescription(parsedContent.description);
					setEditedCost(
						parsedContent.current_estimated_cost?.toString() ||
							parsedContent.estimated_cost?.toString() ||
							''
					);
					setMatchedItem(parsedContent.matched_item || '');
				} catch (parseError) {
					setError('Failed to parse AI response');
					toast.error('Failed to parse AI response');
					onClose();
					setIsSuccess(false);
					console.error('Error parsing AI response:', parseError);
				}
			}
		} catch (error) {
			setError('Failed to process request');
			toast.error('Failed to process request');
			onClose();
			setIsSuccess(false);
			console.error('Error processing request:', error);
		} finally {
			setIsAnalyzing(false);
		}
	};

	const handleUseAIInfo = () => {
		if (onUseAIInfo && editedDescription && editedCost) {
			onUseAIInfo(editedDescription, Number(editedCost), matchedItem);
			setIsSuccess(false); // Reset success state after using the info
			onClose();
		}
	};

	const inputStyle = {
		'& .MuiInputBase-input': {
			lineHeight: 1.5
		},
		'& .MuiInputBase-inputMultiline': {
			padding: 0
		},
		'& .MuiInputBase-input::placeholder': {
			color: '#6d7175',
			opacity: 1
		},
		'& .MuiInputBase-input.Mui-disabled': {
			WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)'
		},
		'& .MuiInputLabel-root.Mui-disabled': {
			color: 'rgba(0, 0, 0, 0.6)'
		}
	};

	return (
		<>
			<Dialog
				open={open}
				maxWidth={aiResponse ? 'md' : 'sm'}
				fullWidth
			>
				{!preAnalyzedData && (
					<DialogTitle>
						Analyze with AI
						<Typography
							variant="subtitle2"
							color="text.secondary"
						>
							{images.length > 0
								? 'Select an image to analyze or use the description.'
								: 'AI analysis results based on your description.'}
						</Typography>
					</DialogTitle>
				)}
				<DialogContent>
					{!preAnalyzedData && (
						<Box
							sx={{
								display: 'flex',
								flexWrap: 'wrap',
								gap: 2,
								justifyContent: 'space-between',
								alignItems: 'center',
								mb: 4
							}}
						>
							{processedImages.length > 0 && (
								<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
									{processedImages.map((image, index) => (
										<Box
											key={index}
											className="rounded-lg border-1 border-gray-300 hover:shadow-lg"
											sx={{
												width: 100,
												height: 100,
												border: selectedImage === index ? '2px solid #ffca00 !important' : '',
												borderRadius: 1,
												overflow: 'hidden',
												cursor: isAnalyzing || !open || isSuccess ? 'not-allowed' : 'pointer',
												opacity:
													(isAnalyzing && selectedImage !== index) || !open || isSuccess
														? 0.5
														: 1,
												position: 'relative',
												'&:hover': {
													borderColor: isAnalyzing || !open || isSuccess ? '#ddd' : '#ffca00'
												},
												transition: 'border-color 0.3s ease'
											}}
											onClick={() => handleImageSelect(index)}
										>
											<img
												src={image}
												alt={`Image ${index + 1}`}
												style={{ width: '100%', height: '100%', objectFit: 'cover' }}
											/>
											{isAnalyzing && selectedImage === index && (
												<Box
													sx={{
														position: 'absolute',
														top: 0,
														left: 0,
														width: '100%',
														height: '100%',
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center',
														backgroundColor: 'rgba(255, 255, 255, 0.8)'
													}}
												>
													<CircularProgress size={20} />
												</Box>
											)}
										</Box>
									))}
								</Box>
							)}
						</Box>
					)}

					{aiResponse && open && (
						<Box>
							<Typography variant="h6">
								{preAnalyzedData
									? 'AI analysis results based on your description:'
									: 'AI Analysis Result:'}
							</Typography>
							<Grid
								container
								spacing={2}
								mt={0}
							>
								{description && (
									<Grid
										item
										xs={12}
										md={6}
									>
										<Typography
											variant="subtitle2"
											color="text.secondary"
											sx={{ mb: 1 }}
										>
											Original Description:
										</Typography>
										<TextField
											fullWidth
											multiline
											rows={5}
											value={description}
											InputProps={{ readOnly: true }}
											variant="outlined"
											sx={inputStyle}
										/>
									</Grid>
								)}
								<Grid
									item
									xs={12}
									md={description ? 6 : 12}
								>
									<Typography
										variant="subtitle2"
										color="text.secondary"
										sx={{ mb: 1 }}
									>
										AI Generated Description:
									</Typography>
									<TextField
										fullWidth
										multiline
										rows={5}
										value={editedDescription}
										onChange={(e) => setEditedDescription(e.target.value)}
										variant="outlined"
										sx={inputStyle}
									/>
								</Grid>
								<Grid
									item
									xs={12}
									md={6}
								>
									<Typography
										variant="subtitle2"
										color="text.secondary"
									>
										Estimated Cost:
									</Typography>
									<TextField
										fullWidth
										type="number"
										value={editedCost}
										onChange={(e) => setEditedCost(e.target.value)}
										variant="outlined"
										sx={{ mt: 1 }}
										InputProps={{
											startAdornment: <InputAdornment position="start">$</InputAdornment>
										}}
									/>
								</Grid>
								<Grid
									item
									xs={12}
									md={6}
								>
									<Typography
										variant="subtitle2"
										color="text.secondary"
									>
										Matched Item:
									</Typography>
									<FormControl fullWidth sx={{ mt: 1 }}>
										<InputLabel id="matched-item-select">Select Item</InputLabel>
										<Select
											labelId="matched-item-select"
											value={matchedItem}
											onChange={(e) => setMatchedItem(e.target.value)}
											label="Select Item"
										>
											<MenuItem value="">
												<em>Select an item</em>
											</MenuItem>
											{items?.map((item) => (
												<MenuItem key={item} value={item}>
													{item}
												</MenuItem>
											))}
											 <MenuItem value="Other">Other</MenuItem>
										</Select>
									</FormControl>
								</Grid>
							</Grid>
						</Box>
					)}
				</DialogContent>
				<DialogActions sx={{ pb: 2 }}>
					{isSuccess && (
						<Button
							variant="contained"
							color="primary"
							onClick={handleUseAIInfo}
							disabled={!editedDescription || !editedCost}
						>
							Use this info
						</Button>
					)}

					{(selectedImage !== null || description) && open && !isSuccess && (
						<Box sx={{ textAlign: 'center' }}>
							<Button
								variant="contained"
								color="secondary"
								onClick={handleAnalyze}
								disabled={isAnalyzing}
								startIcon={isAnalyzing ? <CircularProgress size={20} /> : null}
							>
								Request Analysis
							</Button>
						</Box>
					)}
					<Button onClick={onClose}>Close</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}
